import routeMarkerPicto from '../assets/SVGIcons/Via-Marker.svg';
import { StayDetail } from '../services/restserver-openapi';
import { getReadableCompoundType } from './typeUtils';

declare const H: any;

export type TourWaypointEntry = {
    stayId?: number;
    coordinate: string;
    typeLabel: string;
    title: string;
};

function getCustomStayTypeLabel(stayType?: StayDetail.stayType): string {
    switch (stayType) {
        case 'CUSTOM_START':
            return 'Privater Startort';
        case 'CUSTOM_END':
            return 'Privater Zielort';
        default:
            return 'Privater Zwischenstopp';
    }
}

function getMapBoundaryRank(stay?: StayDetail): number {
    if (stay?.stayType === 'CUSTOM_START') {
        return 0;
    }

    if (stay?.stayType === 'CUSTOM_END') {
        return 2;
    }

    return 1;
}

export function sortStaysForTourMap(stayList: StayDetail[]): StayDetail[] {
    return [...stayList].sort((a, b) => {
        const boundaryRankDifference = getMapBoundaryRank(a) - getMapBoundaryRank(b);
        if (boundaryRankDifference !== 0) {
            return boundaryRankDifference;
        }

        return (a.fromDate ?? 0) - (b.fromDate ?? 0);
    });
}

export function generateTourWaypointEntries(stayList: StayDetail[]) {
    return sortStaysForTourMap(stayList)
        .filter(stay => stay.stayCompound?.coordinates || (stay.customCoordinatesX != null && stay.customCoordinatesY != null))
        .map<TourWaypointEntry>(stay => {
            const x = stay.stayCompound?.coordinates?.x ?? stay.customCoordinatesX;
            const y = stay.stayCompound?.coordinates?.y ?? stay.customCoordinatesY;

            return {
                stayId: stay.stayId,
                coordinate: `${x},${y}`,
                typeLabel: stay.stayType === 'HOST'
                    ? (stay.stayCompound ? getReadableCompoundType(stay.stayCompound) : 'Gastgeber')
                    : getCustomStayTypeLabel(stay.stayType),
                title: stay.stayType === 'HOST'
                    ? stay.stayCompound?.name ?? stay.staySite?.label ?? 'Aufenthalt'
                    : stay.customTitle || stay.customLocationName || stay.customAddressLine1 || 'Privater Aufenthalt',
            };
        });
}

export function calculateZoomLevel(waypoints: string[]) {
    const geoPoints = waypoints.map(wp => wp.split(',').map(Number));
    let zoomLevel = 8;
    let centerLat = 0;
    let centerLng = 0;

    if (geoPoints.length > 1) {
        const lats = geoPoints.map(p => p[0]);
        const lngs = geoPoints.map(p => p[1]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);

        centerLat = (minLat + maxLat) / 2;
        centerLng = (minLng + maxLng) / 2;

        const distance = Math.sqrt(Math.pow(maxLat - minLat, 2) + Math.pow(maxLng - minLng, 2));
        zoomLevel = Math.min(20, 20 - Math.ceil(Math.log(distance / 0.0005) / Math.log(2)));
    } else if (geoPoints.length === 1) {
        [centerLat, centerLng] = geoPoints[0];
        zoomLevel = 14;
    }

    return [zoomLevel, centerLat, centerLng];
}

export function adjustZoomLevel(zoomLevel: number, editorSite: boolean) {
    return editorSite ? zoomLevel - 1 : zoomLevel - 1;
}

export function createRoutingParameter(waypoints: string[]) {
    const startpoint = waypoints[0];
    const endpoint = waypoints[waypoints.length - 1];
    const viaPoints = waypoints.slice(1, -1).filter((wp) => {
        const parts = wp.split(',');
        return parts.length === 2 && !isNaN(+parts[0]) && !isNaN(+parts[1]);
    });

    const routingParams: any = {
        origin: startpoint,
        destination: endpoint,
        routingMode: 'fast',
        transportMode: 'car',
        return: 'polyline,summary'
    };

    if (viaPoints.length > 0) {
        routingParams.via = new H.service.Url.MultiValueQueryParameter(viaPoints);
        routingParams.passthrough = 'true';
    }

    return routingParams;
}

export function generateTourCoordinateList(stayList: StayDetail[]) {
    return generateTourWaypointEntries(stayList).map((entry) => entry.coordinate);
}

export function calculateDistance(routes: any) {
    if (!routes?.sections) return 0;
    return Math.round(
        routes.sections.reduce((sum: number, section: any) => sum + (section.summary?.length || 0), 0) / 1000 * 100
    ) / 100;
}

export function calculateDistanceList(routes: any) {
    if (!routes?.sections) return [];
    return routes.sections.map((section: any) =>
        Math.round(((section.summary?.length || 0) / 1000) * 100) / 100
    );
}

export function addDraggableMarker(
    map: any,
    behavior: any,
    lat: number,
    lng: number,
    dragEndCallback: (lat: number, lng: number) => void
) {
    map.removeObjects(map.getObjects());

    const icon = new H.map.Icon(routeMarkerPicto, {
        size: { w: 32, h: 32 },
        anchor: { x: 16, y: 32 }
    });

    const marker = new H.map.Marker({ lat, lng }, {
        volatility: true,
        icon
    });
    marker.draggable = true;
    map.addObject(marker);

    map.addEventListener('dragstart', function (ev: any) {
        const target = ev.target;
        if (target instanceof H.map.Marker) {
            const position = map.geoToScreen(target.getGeometry());
            if (!position) return;
            target.offset = new H.math.Point(
                ev.currentPointer.viewportX - position.x,
                ev.currentPointer.viewportY - position.y
            );
            behavior.disable();
        }
    }, false);

    map.addEventListener('drag', function (ev: any) {
        const target = ev.target;
        if (target instanceof H.map.Marker && target.offset) {
            const geo = map.screenToGeo(
                ev.currentPointer.viewportX - target.offset.x,
                ev.currentPointer.viewportY - target.offset.y
            );
            if (geo) {
                target.setGeometry(geo);
            }
        }
    }, false);

    map.addEventListener('dragend', function (ev: any) {
        const target = ev.target;
        if (target instanceof H.map.Marker) {
            const geo = target.getGeometry();
            dragEndCallback(+geo.lat.toFixed(5), +geo.lng.toFixed(5));
            behavior.enable();
        }
    }, false);

    map.setCenter({ lat, lng });
    map.setZoom(14);
}
